import React, { useState, useEffect, useCallback } from 'react';
import QuizSettingsForm from '../components/QuizSettingsForm';

const DEFAULT_SETTINGS = {
  name: '',
  repeatable: false,
  hideAnswers: false,
  welcomeText: '',
  descriptionText: '',
  successText: '',
  failText: '',
  threshold: 80,
};

/**
 * useQuizSettingsEditor - Hook for managing quiz settings form state
 * Provides form component, state, and handlers for modal/panel usage
 *
 * @param {Object} options
 * @param {Object} options.quizSettings - Current quiz settings
 * @param {string} options.quizName - Fallback quiz name (from section/page)
 * @param {Function} options.onSave - Callback when settings are saved
 * @param {boolean} options.compact - Use compact layout (default: false)
 * @param {string} options.inputSize - Size for inputs: 'small' | 'medium' (default: 'medium')
 * @param {boolean} options.isEditing - Whether editing existing quiz (hides name field)
 * @returns {Object} { form, settings, handleSave, handleChange }
 */
const useQuizSettingsEditor = ({ quizSettings, quizName, onSave, compact = false, inputSize = 'medium', isEditing = false }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    if (quizSettings) {
      setSettings({
        name: quizSettings.name || quizName || '',
        repeatable: quizSettings.repeatable || false,
        hideAnswers: quizSettings.hideAnswers || false,
        welcomeText: quizSettings.welcomeText || '',
        descriptionText: quizSettings.descriptionText || '',
        successText: quizSettings.successText || '',
        failText: quizSettings.failText || '',
        threshold: quizSettings.threshold || 80,
      });
    } else if (quizName) {
      setSettings((prev) => ({ ...prev, name: quizName }));
    }
  }, [quizSettings, quizName]);

  const handleChange = useCallback((field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(settings);
    }
  }, [onSave, settings]);

  const form = <QuizSettingsForm settings={settings} onChange={handleChange} compact={compact} inputSize={inputSize} isEditing={isEditing} />;

  return { form, settings, handleSave, handleChange };
};

export default useQuizSettingsEditor;
